import "./gallery.scss";
import { useEffect, useState, useCallback, useRef } from "react";
import LoadingOverlay from "../../components/loading/LoadingOverlay";

export default function Gallery() {
  const [allFilenames, setAllFilenames] = useState([]); // all filenames
  const [thumbnailUrls, setThumbnailUrls] = useState([]); // currently loaded thumbnails
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [nextIndex, setNextIndex] = useState(0);

  const INITIAL_LOAD = 22;
  const LOAD_CHUNK = 22;

  const closeX = {
    src: `${process.env.PUBLIC_URL}/images/common/close-x.svg`,
    alt: "close x icon",
  };
  const [imagesLoaded, setImagesLoaded] = useState(false);

  // Fetch all filenames on mount
  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/images/gallery/images.json`)
      .then((res) => res.json())
      .then((filenames) => {
        setAllFilenames(filenames);

        const initialThumbs = filenames
          .slice(0, INITIAL_LOAD)
          .map(
            (file) =>
              `${process.env.PUBLIC_URL}/images/gallery/thumbnails/${file}`
          );
        setThumbnailUrls(initialThumbs);
        setNextIndex(INITIAL_LOAD);
      })
      .catch((err) => console.error("Error fetching image filenames:", err));
  }, []);

  // Load more thumbnails
  const loadMoreThumbnails = useCallback(() => {
    if (nextIndex >= allFilenames.length) return;

    const end = Math.min(nextIndex + LOAD_CHUNK, allFilenames.length);
    const moreThumbs = allFilenames
      .slice(nextIndex, end)
      .map(
        (file) => `${process.env.PUBLIC_URL}/images/gallery/thumbnails/${file}`
      );

    setThumbnailUrls((prev) => [...prev, ...moreThumbs]);
    setNextIndex(end);
  }, [nextIndex, allFilenames]);

  // Infinite scroll
  const scrollElRef = useRef(null);

  useEffect(() => {
    const root = document.getElementById("root");

    // Decide which element actually scrolls
    const scrollElement =
      root && root.scrollHeight > root.clientHeight ? root : window;

    scrollElRef.current = scrollElement;

    const handleScroll = () => {
      let scrollTop, scrollHeight, clientHeight;

      if (scrollElement === window) {
        scrollTop =
          window.pageYOffset || document.documentElement.scrollTop || 0;
        scrollHeight = document.documentElement.scrollHeight;
        clientHeight = window.innerHeight;
      } else {
        scrollTop = scrollElement.scrollTop;
        scrollHeight = scrollElement.scrollHeight;
        clientHeight = scrollElement.clientHeight;
      }

      const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);

      if (distanceFromBottom < 500) {
        loadMoreThumbnails();
      }
    };

    scrollElement.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => scrollElement.removeEventListener("scroll", handleScroll);
  }, [loadMoreThumbnails]);

  const closeModal = useCallback(() => setSelectedImageIndex(null), []);

  const showPrev = useCallback(() => {
    setSelectedImageIndex((prev) =>
      prev === 0 ? allFilenames.length - 1 : prev - 1
    );
  }, [allFilenames.length]);

  const showNext = useCallback(() => {
    setSelectedImageIndex((prev) =>
      prev === allFilenames.length - 1 ? 0 : prev + 1
    );
  }, [allFilenames.length]);

  useEffect(() => {
    if (thumbnailUrls.length > 10) {
      const timeoutId = setTimeout(() => {
        setImagesLoaded(true);
      }, 1500);
      return () => clearTimeout(timeoutId);
    }
  });

  return (
    <>
      <LoadingOverlay isLoading={!imagesLoaded} />
      <div
        className={`gallery container padding-bottom-50 mobile-gallery-bg ${
          imagesLoaded ? "fade-in" : ""
        }`}
      >
        <section className="gallery-banner text-center width-100 position-relative display-flex flex-column align-center justify-center">
          <h1 className="cormorant-500 font-size-8 letter-spacing-3 position-relative margin-0 text-medium">
            GALLERY
          </h1>
        </section>

        {/* Thumbnail grid */}
        <section className="gallery-images-container">
          {thumbnailUrls.map((thumbUrl) => {
            const fileName = thumbUrl.split("/").pop();
            const fullIndex = allFilenames.indexOf(fileName);

            return (
              <div key={thumbUrl} className="gallery-item">
                <img
                  src={thumbUrl}
                  alt={`Gallery thumbnail ${fullIndex}`}
                  loading="lazy"
                  width={200}
                  height={200}
                  onClick={() => setSelectedImageIndex(fullIndex)}
                />
              </div>
            );
          })}
        </section>

        {/* Modal */}
        {selectedImageIndex !== null && (
          <div className="image-modal" onClick={closeModal}>
            <div
              className="modal-content gallery-polaroid"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="image-container">
                <img
                  src={`${process.env.PUBLIC_URL}/images/gallery/${allFilenames[selectedImageIndex]}`}
                  alt="Full size"
                  loading="lazy"
                />
              </div>

              <div className="arrow-container">
                <button className="garamond-500 arrow" onClick={showPrev}>
                  &#10094; Prev
                </button>
                <button className="garamond-500 arrow" onClick={showNext}>
                  Next &#10095;
                </button>
              </div>

              <button className="close-modal" onClick={closeModal}>
                <img
                  className="close-x"
                  src={closeX.src}
                  alt={closeX.alt}
                  width="24"
                  height="24"
                  loading="lazy"
                />
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
